import {
  CallDto,
  VoiceCallAuditDto,
  VoiceCallRecordingDto,
} from '@act/shared/data-transfer-objects';
import { createEntityAdapter } from '@reduxjs/toolkit';
import { CallState } from '../types/callsTypes';

export const callsAdapter = createEntityAdapter<CallDto>();
export const voiceCallReviewAdapter = createEntityAdapter<VoiceCallAuditDto>();

export const callsInitialState: CallState = {
  callPageIds: [],
  callPageTotalCount: 0,
  calls: callsAdapter.getInitialState({
    status: 'initial',
  }),
  voiceCallReviews: voiceCallReviewAdapter.getInitialState({
    status: 'initial',
  }),
  callFilterOptions: null,
  callFilterOptionStatus: 'initial',
  justInTimeCalls: {},
  suggestions: {},
  suggestionsLoadingStatus: {},
};

/**
 * Whether a recording DTO carries generated transcript/summary content.
 * @param recording Recording payload, if any.
 * @returns True when summary, transcript, or generated notes are present.
 */
function recordingHasGeneratedContent(
  recording: VoiceCallRecordingDto | null | undefined
): boolean {
  return !!(
    recording?.summary ||
    recording?.recordingTranscription ||
    recording?.llmGeneratedNotes
  );
}

/**
 * Whether a recording DTO proves a call was captured even before transcription.
 * @param recording Recording payload, if any.
 * @returns True when a persisted audio file id is present.
 */
function recordingHasArtifact(
  recording: VoiceCallRecordingDto | null | undefined
): boolean {
  return !!recording?.fileId;
}

/**
 * Merges recording DTOs, preferring populated incoming fields over existing ones.
 * @param existing Recording currently stored on the call, if any.
 * @param incoming Recording from the update payload, if any.
 * @returns Combined recording, or null when neither side has one.
 */
function mergeRecordingDto(
  existing: VoiceCallRecordingDto | null | undefined,
  incoming: VoiceCallRecordingDto | null | undefined
): VoiceCallRecordingDto | null {
  if (incoming == null) {
    return existing ?? null;
  }
  if (existing == null) {
    return incoming;
  }
  return { ...existing, ...incoming };
}

/**
 * Merges an incoming call update while preserving recording fields when omitted.
 * @param existing Call currently stored in Redux.
 * @param incoming Call payload from the API or websocket.
 * @returns Merged call safe to store in Redux.
 */
export function mergeCallUpdatePreservingRecording(
  existing: CallDto,
  incoming: CallDto
): CallDto {
  const merged: CallDto = { ...existing, ...incoming };

  // A partial update whose payload didn't load the internal-phone-number relation reports
  // shouldRecord as null (unknown). Don't let that overwrite a value we already know, or a
  // recording-enabled call could be flipped to a false "not recorded" state.
  if (incoming.shouldRecord == null && existing.shouldRecord != null) {
    merged.shouldRecord = existing.shouldRecord;
  }

  if (incoming.recording != null) {
    return {
      ...merged,
      recording: mergeRecordingDto(existing.recording, incoming.recording),
    };
  }

  // CALL_UPDATED websocket events omit the recording relation, so preserve any real
  // recording we already have when an update doesn't carry one. `fileId` covers a call
  // that was recorded but not transcribed (e.g. a short call skipped for auto-processing),
  // which has no summary/transcription/notes yet still must not be wiped back to null.
  if (
    existing.recording != null &&
    (recordingHasGeneratedContent(existing.recording) ||
      recordingHasArtifact(existing.recording))
  ) {
    return { ...merged, recording: existing.recording };
  }

  return merged;
}
